import { useEffect, useState } from 'react';
import { Card, Tag, Typography, Alert, Button, Spin } from 'antd';
import {
  ShopOutlined,
  RightOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useSelectContextMutation, useContextsQuery, useSwitchContextMutation } from './hooks';
import { ApiError } from '@starling/core/api/errors';
import type { components } from '@/api/types.gen';

const { Title, Text } = Typography;

type ContextOption = components['schemas']['ContextOption'];

/**
 * State, передаваемый в `/select-context` через location.state (в памяти).
 * select_token НЕ кладём в localStorage — он короткоживущий (5 мин). Рефреш
 * страницы теряет state → редирект на /login (см. эффект-гард ниже).
 */
export interface ContextPickerState {
  select_token: string;
  contexts: ContextOption[];
  email?: string;
}

// ── Чип роли: зеркало RoleTag из users/UsersList.tsx. admin — синий, иначе дефолт.
function RoleChip({ role }: { role: ContextOption['role'] }): JSX.Element {
  const { t } = useTranslation();
  if (role === 'admin') return <Tag color="blue">{t('users.role.admin')}</Tag>;
  return <Tag>{t('users.role.employee')}</Tag>;
}

// ── Строка контекста: иконка магазина + название оптики + чип роли. Кликабельна.
function ContextRow({
  ctx,
  loading,
  dimmed,
  onPick,
}: {
  ctx: ContextOption;
  loading: boolean;
  dimmed: boolean;
  onPick: () => void;
}): JSX.Element {
  const [hover, setHover] = useState(false);
  const active = hover && !dimmed && !loading;

  return (
    <button
      type="button"
      onClick={onPick}
      disabled={dimmed || loading}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '13px 14px',
        textAlign: 'left',
        borderRadius: 'var(--radius-md)',
        border: '1px solid ' + (active || loading ? 'var(--accent)' : 'var(--hairline)'),
        background: active ? 'var(--mist)' : 'var(--surface)',
        boxShadow: active ? 'var(--shadow-1)' : 'none',
        cursor: dimmed || loading ? 'default' : 'pointer',
        opacity: dimmed ? 0.45 : 1,
        transition:
          'border-color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out), opacity var(--dur-fast) var(--ease-out)',
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 'var(--radius-sm)',
          flexShrink: 0,
          background: 'var(--mist)',
          border: '1px solid var(--hairline)',
          color: 'var(--ink-muted)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 17,
        }}
      >
        <ShopOutlined />
      </span>

      <span style={{ flex: 1, minWidth: 0, display: 'block' }}>
        <span
          title={ctx.tenant_name}
          style={{
            display: 'block',
            font: '700 15px/1.35 var(--font-sans)',
            color: 'var(--ink)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {ctx.tenant_name}
        </span>
        <span style={{ display: 'block', marginTop: 5 }}>
          <RoleChip role={ctx.role} />
        </span>
      </span>

      {loading ? (
        <LoadingOutlined style={{ fontSize: 17, color: 'var(--accent)', flexShrink: 0 }} />
      ) : (
        <RightOutlined
          style={{
            fontSize: 15,
            flexShrink: 0,
            color: active ? 'var(--accent)' : 'var(--ink-faint)',
          }}
        />
      )}
    </button>
  );
}

// ── Select-token истёк (5 мин) — выбор больше не принимается, только на /login.
function ExpiredCard(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Card style={{ width: '100%', maxWidth: 480, textAlign: 'center' }} variant="borderless">
      <span
        style={{
          width: 48,
          height: 48,
          borderRadius: 'var(--radius-md)',
          margin: '0 auto 14px',
          background: '#FCEEDB',
          color: 'var(--warning)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 24,
        }}
      >
        <ClockCircleOutlined />
      </span>
      <Title level={3} style={{ marginBottom: 6 }}>
        {t('auth.context_picker.expired.title')}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
        {t('auth.context_picker.expired.description')}
      </Text>
      <Button
        type="primary"
        block
        size="large"
        onClick={() => navigate('/login', { replace: true })}
      >
        {t('auth.context_picker.expired.retry')}
      </Button>
    </Card>
  );
}

// Общая центрирующая обёртка страницы пикера (mist-фон, карточка по центру).
function PickerShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--mist)',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Экран выбора контекста (оптики). Два режима:
 *
 *   * `login` (по умолчанию, public `/select-context`) — «Войти как…» в потоке
 *     входа: данные из location.state (select_token + contexts), выбор →
 *     `POST /auth/select-context`, на 401 состояние «истекло», ссылка «Войти
 *     другим аккаунтом».
 *   * `switch` (authed `/switch-context`) — «Сменить оптику» мид-сессионно:
 *     contexts из `GET /auth/contexts`, выбор → `POST /auth/switch-context`
 *     (без select_token), успех → назад в кабинет `/`. «Истекло»/«Войти другим»
 *     не применяются; вместо них — «Отмена» (назад в кабинет).
 */
export function ContextPickerPage({
  mode = 'login',
}: {
  mode?: 'login' | 'switch';
} = {}): JSX.Element {
  if (mode === 'switch') return <SwitchContextView />;
  return <LoginContextView />;
}

/** Switch-режим: смена оптики без перелогина (authed). */
function SwitchContextView(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useContextsQuery();
  const mutation = useSwitchContextMutation();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [networkError, setNetworkError] = useState(false);

  const contexts = data?.contexts ?? [];
  const busy = pendingId != null;
  const goBack = (): void => navigate('/', { replace: true });

  const pick = (ctx: ContextOption): void => {
    if (busy) return;
    setNetworkError(false);
    setPendingId(ctx.membership_id);
    mutation.mutate(
      { membership_id: ctx.membership_id },
      {
        onSuccess: () => {
          // qc.clear() + setTokens отработали в хуке — остаёмся в приложении,
          // гарды в App.tsx разрулят роль (platform_admin → /platform).
          navigate('/', { replace: true });
        },
        onError: () => {
          setPendingId(null);
          setNetworkError(true);
        },
      },
    );
  };

  return (
    <PickerShell>
      <Card style={{ width: '100%', maxWidth: 480 }} variant="borderless">
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('auth.context_picker.switch_title')}
          </Title>
        </div>

        {networkError && (
          <Alert
            type="error"
            showIcon
            role="alert"
            style={{ marginBottom: 16 }}
            message={t('auth.context_picker.error_network')}
          />
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Spin />
          </div>
        ) : isError ? (
          <Alert
            type="error"
            showIcon
            role="alert"
            message={t('auth.context_picker.error_network')}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              maxHeight: 340,
              overflowY: 'auto',
              margin: '0 -4px',
              padding: '0 4px',
            }}
          >
            {contexts.map((ctx) => (
              <ContextRow
                key={ctx.membership_id}
                ctx={ctx}
                loading={pendingId === ctx.membership_id}
                dimmed={busy && pendingId !== ctx.membership_id}
                onPick={() => pick(ctx)}
              />
            ))}
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Button type="link" onClick={goBack} disabled={busy}>
            {t('common.cancel')}
          </Button>
        </div>
      </Card>
    </PickerShell>
  );
}

/**
 * Login-режим `/select-context` — экран «Войти как…».
 *
 * Появляется в потоке логина, когда у личности 2+ активных membership (M7.2).
 * Данные (select_token + contexts + email) приходят из LoginPage через
 * location.state. Клик по строке обменивает выбор на пару токенов через
 * `POST /auth/select-context`; на 401 (истёкший/битый select_token) —
 * состояние «Время выбора истекло».
 */
function LoginContextView(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ContextPickerState | null;

  const [pendingId, setPendingId] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  const mutation = useSelectContextMutation();

  // Нет state (прямой заход / рефреш потерял select_token) — на /login.
  useEffect(() => {
    if (!state) {
      navigate('/login', { replace: true });
    }
  }, [state, navigate]);

  if (!state) {
    return <div style={{ minHeight: '100vh', background: 'var(--mist)' }} />;
  }

  if (expired) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'var(--mist)',
        }}
      >
        <ExpiredCard />
      </div>
    );
  }

  const busy = pendingId != null;

  const pick = (ctx: ContextOption): void => {
    if (busy) return;
    setNetworkError(false);
    setPendingId(ctx.membership_id);
    mutation.mutate(
      { select_token: state.select_token, membership_id: ctx.membership_id },
      {
        onSuccess: () => {
          // setTokens сработал в хуке — уводим в кабинет. Гарды в App.tsx
          // разрулят роль (platform_admin → /platform).
          navigate('/', { replace: true });
        },
        onError: (err) => {
          setPendingId(null);
          // 401 — select_token протух/битый либо чужой membership. Повторно его
          // использовать нельзя → показываем состояние «Время выбора истекло».
          if (err instanceof ApiError && err.status === 401) {
            setExpired(true);
            return;
          }
          // Сеть или иная ошибка — оставляем список, показываем alert.
          setNetworkError(true);
        },
      },
    );
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--mist)',
      }}
    >
      <Card style={{ width: '100%', maxWidth: 480 }} variant="borderless">
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('auth.context_picker.title')}
          </Title>
          {state.email && <Text type="secondary">{state.email}</Text>}
        </div>

        {networkError && (
          <Alert
            type="error"
            showIcon
            role="alert"
            style={{ marginBottom: 16 }}
            message={t('auth.context_picker.error_network')}
          />
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            // Много оптик не влезают — список скроллится, карточка на месте.
            maxHeight: 340,
            overflowY: 'auto',
            margin: '0 -4px',
            padding: '0 4px',
          }}
        >
          {state.contexts.map((ctx) => (
            <ContextRow
              key={ctx.membership_id}
              ctx={ctx}
              loading={pendingId === ctx.membership_id}
              dimmed={busy && pendingId !== ctx.membership_id}
              onPick={() => pick(ctx)}
            />
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Link to="/login">{t('auth.context_picker.login_other')}</Link>
        </div>
      </Card>
    </div>
  );
}
